import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useSettings } from '../contexts/SettingsContext';
import Icon from './AppIcon';

const AudioPlayer: React.FC = () => {
  // All hooks must be called unconditionally (Rules of Hooks)
  const { primaryColor, radioStreamUrl, loading: settingsLoading } = useSettings();
  const location = useLocation();
  const { audio, isPlaying, play, pause } = useAudioPlayer();
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const sourceCreatedRef = useRef<boolean>(false);
  const [spectrumData, setSpectrumData] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Check if current route is an auth route
  const isAuthRoute = () => {
    const path = location.pathname.toLowerCase();
    return (
      path === '/login' ||
      path === '/register' ||
      path === '/reset-password' ||
      path === '/authentication-hub' ||
      path.startsWith('/auth/')
    );
  };

  // Initialize Web Audio API for spectrum visualization
  // MUST be called before any conditional returns (Rules of Hooks)
  useEffect(() => {
    // Don't run if no URL configured
    if (!radioStreamUrl || radioStreamUrl.trim() === '') {
      return;
    }

    if (!audio || !isPlaying) {
      setSpectrumData([]);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const initAudioContext = async () => {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContextClass();
        }

        // Create media element source only once
        if (!sourceCreatedRef.current && audio) {
          try {
            // Wait for audio to be ready
            if (audio && audio.readyState >= 2) {
              try {
                const source = audioContextRef.current.createMediaElementSource(audio);
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = 256;
                analyserRef.current.smoothingTimeConstant = 0.8;
                
                source.connect(analyserRef.current);
                analyserRef.current.connect(audioContextRef.current.destination);
                
                const bufferLength = analyserRef.current.frequencyBinCount;
                dataArrayRef.current = new Uint8Array(bufferLength);
                sourceCreatedRef.current = true;
              } catch (err) {
                console.warn('Failed to create media element source:', err);
              }
            } else if (audio) {
              // Wait for audio to be ready
              const onCanPlay = () => {
                if (audioContextRef.current && audio && !sourceCreatedRef.current) {
                  try {
                    const source = audioContextRef.current.createMediaElementSource(audio);
                    analyserRef.current = audioContextRef.current.createAnalyser();
                    analyserRef.current.fftSize = 256;
                    analyserRef.current.smoothingTimeConstant = 0.8;
                    
                    source.connect(analyserRef.current);
                    analyserRef.current.connect(audioContextRef.current.destination);
                    
                    const bufferLength = analyserRef.current.frequencyBinCount;
                    dataArrayRef.current = new Uint8Array(bufferLength);
                    sourceCreatedRef.current = true;
                    audio.removeEventListener('canplay', onCanPlay);
                  } catch (err) {
                    console.warn('Failed to create media element source:', err);
                  }
                }
              };
              audio.addEventListener('canplay', onCanPlay);
            }
          } catch (err) {
            // MediaElementSource already created - this is fine
            if (err instanceof Error && err.message.includes('already been connected')) {
              sourceCreatedRef.current = true;
            } else {
              console.warn('Web Audio API source creation failed:', err);
            }
          }
        }

        // Resume audio context if suspended
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
      } catch (err) {
        console.warn('Web Audio API initialization failed:', err);
      }
    };

    initAudioContext();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [audio, isPlaying, radioStreamUrl]);

  // Update spectrum visualization
  useEffect(() => {
    // Don't run if no audio or URL not configured
    if (!audio || !radioStreamUrl || radioStreamUrl.trim() === '') {
      setSpectrumData([]);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    if (!isPlaying) {
      setSpectrumData([]);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const updateSpectrum = () => {
      if (!isPlaying || !audio) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      if (!analyserRef.current || !dataArrayRef.current) {
        // If analyser not ready, show placeholder spectrum with animation
        const placeholderBars = Array.from({ length: 8 }, (_, i) => {
          const base = 20 + Math.sin(Date.now() / 200 + i) * 15;
          return Math.max(base, 10);
        });
        setSpectrumData(placeholderBars);
        animationFrameRef.current = requestAnimationFrame(updateSpectrum);
        return;
      }

      if (!dataArrayRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateSpectrum);
        return;
      }

      if (!analyserRef.current || !dataArrayRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateSpectrum);
        return;
      }

      // Type assertion needed due to TypeScript strictness with ArrayBuffer types
      analyserRef.current.getByteFrequencyData(dataArrayRef.current as any);
      
      // Take 8 bars for visualization
      const barCount = 8;
      const step = Math.floor(dataArrayRef.current.length / barCount);
      const bars: number[] = [];
      
      for (let i = 0; i < barCount; i++) {
        const index = i * step;
        const value = dataArrayRef.current[index];
        bars.push((value / 255) * 100);
      }
      
      setSpectrumData(bars);
      
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateSpectrum);
      }
    };

    // Start the animation loop
    updateSpectrum();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlaying, audio, radioStreamUrl]);

  const handleTogglePlay = async () => {
    // Don't do anything if no URL configured or no audio
    if (!radioStreamUrl || radioStreamUrl.trim() === '') {
      console.warn('AudioPlayer: No radio stream URL configured');
      return;
    }
    
    if (!audio) {
      console.warn('AudioPlayer: Audio element is null');
      return;
    }

    if (isPlaying) {
      pause();
    } else {
      setIsLoading(true);
      try {
        await play();
      } catch (error) {
        console.warn('AudioPlayer: Failed to play:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Hide while settings are loading (prevents hook count mismatch)
  if (settingsLoading) {
    return null;
  }

  // Hide on auth routes
  if (isAuthRoute()) {
    return null;
  }

  // Hide if radio stream URL is not configured
  if (!radioStreamUrl || radioStreamUrl.trim() === '') {
    if (import.meta.env.DEV) {
      console.warn('AudioPlayer: Radio stream URL not configured or empty');
    }
    return null;
  }

  return (
    <div 
      className="fixed z-50 group"
      style={{ 
        bottom: '20px',
        left: '20px',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Player Container - Only round button */}
      <div className="relative">
        {/* Play/Pause Button */}
        <button
          onClick={handleTogglePlay}
          disabled={isLoading}
          className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg relative overflow-hidden"
          style={{
            backgroundColor: primaryColor,
            color: '#ffffff',
          }}
          aria-label={isPlaying ? 'Pause radio' : 'Play radio'}
        >
          {/* Wave Spectrum Visualization - Inside the button when playing and not hovered */}
          {isPlaying && !isHovered && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-2">
              <div className="flex items-end justify-center gap-0.5 h-full">
                {spectrumData.length > 0 ? (
                  spectrumData.map((height, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-full transition-all duration-75"
                      style={{
                        width: '2px',
                        height: `${Math.max((height / 100) * 60, 8)}%`,
                        minHeight: '2px',
                        opacity: 0.9,
                      }}
                    />
                  ))
                ) : (
                  // Placeholder wave bars while initializing
                  Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-full animate-pulse"
                      style={{
                        width: '2px',
                        height: `${15 + (index % 4) * 8}%`,
                        minHeight: '2px',
                        opacity: 0.6,
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Icons - Show when not playing, loading, or when hovered while playing */}
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying && isHovered ? (
            // Show pause button when playing and hovered
            <Icon name="Pause" size={20} />
          ) : !isPlaying ? (
            // Show play icon when not playing
            <Icon name="Play" size={20} />
          ) : null}
        </button>

        {/* Tooltip - Positioned to the right, vertically centered, outside button */}
        <div className="absolute left-full ml-3 top-1/2 transform -translate-y-1/2 px-3 py-1.5 bg-foreground text-background text-xs font-medium rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          Sisi ni Tanzania Radio
          <div className="absolute right-full top-1/2 transform -translate-y-1/2 -mr-px">
            <div className="border-4 border-transparent border-r-foreground"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;

